import React from "react";

import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import type { Project } from "@domains/project/types";
import {
  activityLabel,
  activityDotColor,
} from "@domains/project/utils/activityLabels";
import type { ResourceActivityEntryUI } from "@domains/resource/types";

export interface ProjectOverviewSidebarProps {
  project: Project;
  activity: ResourceActivityEntryUI[];
  activityLoading: boolean;
  onViewAllActivity?: () => void;
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Card wrapper matching the mockup's `rusha-card` pattern. */
function SidebarCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        p: 2.5,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        mb: 2.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "text.secondary",
          }}
        >
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

/** Meta row matching the mockup's `rusha-meta-row` pattern. */
function MetaRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: "13px",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography color="text.secondary" fontSize="13px">
        {label}
      </Typography>
      <Typography fontSize="13px">{value}</Typography>
    </Stack>
  );
}

/**
 * Project Overview sidebar — two separate cards:
 * 1. Project Overview (meta rows: Owner, Created, Region, Environments)
 * 2. Recent Activity (activity feed with View all action)
 */
export function ProjectOverviewSidebar({
  project,
  activity,
  activityLoading,
  onViewAllActivity,
}: ProjectOverviewSidebarProps): React.ReactElement {
  const theme = useTheme();
  const orgName = project.organization?.name ?? "—";
  const created = project.createdAt
    ? new Date(project.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

  return (
    <Box>
      {/* Project Overview card */}
      <SidebarCard title="Project Overview">
        <MetaRow label="Owner" value={orgName} />
        <MetaRow label="Created" value={created} />
        <MetaRow label="Region" value="eu-central-1" />
        <MetaRow label="Environments" value="dev, staging, production" />
      </SidebarCard>

      {/* Recent Activity card */}
      <SidebarCard
        title="Recent Activity"
        action={
          onViewAllActivity && (
            <Button
              size="small"
              onClick={onViewAllActivity}
              sx={{
                fontSize: "12px",
                color: "primary.main",
                fontWeight: 600,
                textTransform: "none",
                p: 0,
              }}
            >
              View all
            </Button>
          )
        }
      >
        {activityLoading && activity.length === 0 ? (
          <Stack spacing={2}>
            <Skeleton variant="text" height={32} />
            <Skeleton variant="text" height={32} />
            <Skeleton variant="text" height={32} />
          </Stack>
        ) : activity.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No activity yet
          </Typography>
        ) : (
          <Stack>
            {activity.slice(0, 5).map((entry) => (
              <Stack
                key={entry.id}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  py: 1.25,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: activityDotColor(entry.event),
                    mt: 1,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography variant="body2">
                    {activityLabel(entry.event)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {relativeTime(entry.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </SidebarCard>
    </Box>
  );
}
